import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Layout, Button, Text} from '@phonectas/unifon-ui-kit';
import {RES_ACC_OPTIONAL_FIELDS} from '../constants';

interface TransferListProps {
    selected: string[];
    onChange: (newSelected: string[]) => void;
}

export const TransferList: React.FC<TransferListProps> = ({
	selected,
	onChange,
}) => {
	const [t] = useTranslation();
	const allValues = useMemo(
		() => RES_ACC_OPTIONAL_FIELDS.flatMap(group => group.fields.map(f => f.value)),
		[]
	);

	const availableGroups = useMemo(() =>
		RES_ACC_OPTIONAL_FIELDS.map(group => ({
			subtitle: group.subtitle,
			fields: group.fields.filter(f => !selected.includes(f.value)),
		})).filter(group => group.fields.length > 0),
	[selected]
	);

	const selectedGroups = useMemo(() =>
		RES_ACC_OPTIONAL_FIELDS.map(group => ({
			subtitle: group.subtitle,
			fields: group.fields.filter(f => selected.includes(f.value)),
		})).filter(group => group.fields.length > 0),
	[selected]
	);

	const handleSelectItem = useCallback((value: string) => {
		if (!selected.includes(value)) {
			onChange([...selected, value]);
		}
	}, [selected, onChange]);

	const handleSelectGroup = useCallback((subtitle: string) => {
		const group = RES_ACC_OPTIONAL_FIELDS.find(g => g.subtitle === subtitle);
		if (group) {
			const newValues = group.fields.map(f => f.value).filter(v => !selected.includes(v));
			onChange([...selected, ...newValues]);
		}
	}, [selected, onChange]);

	const handleRemoveItem = useCallback((value: string) => {
		onChange(selected.filter(v => v !== value));
	}, [selected, onChange]);

	const handleRemoveGroup = useCallback((subtitle: string) => {
		const group = RES_ACC_OPTIONAL_FIELDS.find(g => g.subtitle === subtitle);
		if (group) {
			const removeValues = group.fields.map(f => f.value);
			onChange(selected.filter(v => !removeValues.includes(v)));
		}
	}, [selected, onChange]);

	const handleMoveAll = useCallback(() => onChange(allValues), [allValues, onChange]);
	const handleRemoveAll = useCallback(() => onChange([]), [onChange]);

	return (
		<Layout direction="row" fullWidth nowrap gap="lg">
			{/* Main List */}
			<Layout
				direction="column"
				nowrap
				style={{width: selected.length > 0 ? '100%' : '444px'}}
			>
				<Text variant="h2">{t('Available fields')}</Text>
				<Button variant="secondary" onClick={handleMoveAll} style={{marginBottom: 8}}>
					{t('Move all')}
				</Button>
				{availableGroups.map(group => (
					<div key={group.subtitle}>
						<span
							style={{
								cursor: 'pointer',
								fontWeight: 600,
								marginBottom: 8,
								display: 'block'
							}}
							onClick={() => handleSelectGroup(group.subtitle)}
							role="button"
							tabIndex={0}
						>
							<Text variant="subtitle2">{group.subtitle}</Text>
						</span>
						{group.fields.map(field => (
							<span
								key={field.value}
								style={{
									cursor: 'pointer',
									marginLeft: 10,
									marginBottom: 2,
									display: 'block'
								}}
								onClick={() => handleSelectItem(field.value)}
								role="button"
								tabIndex={0}
							>
								<Text>{field.label}</Text>
							</span>
						))}
					</div>
				))}
			</Layout>

			{/* Selected List (slides in if any selected) */}
			{selected.length > 0 && (
				<Layout
					direction="column"
					nowrap
					fullWidth
					style={{
						minWidth: 300,
						transition: 'transform 0.3s',
						transform: selected.length > 0 ? 'translateX(0)' : 'translateX(100%)',
						boxShadow: '0 0 8px rgba(0,0,0,0.08)',
						background: '#f9f9f9',
						borderRadius: 8,
						padding: '0 0 0 10px',
					}}
				>
					<Text variant="h2">{t('Selected Fields')}</Text>
					<Button variant="secondary" onClick={handleRemoveAll} style={{marginBottom: 8}}>
						{t('Remove All')}
					</Button>
					{selectedGroups.map(group => (
						<div key={group.subtitle}>
							<span
								style={{
									cursor: 'pointer',
									fontWeight: 600,
									marginBottom: 8,
									display: 'block',
								}}
								onClick={() => handleRemoveGroup(group.subtitle)}
								role="button"
								tabIndex={0}
							>
								<Text variant="subtitle2">{group.subtitle}</Text>
							</span>
							{group.fields.map(field => (
								<span
									key={field.value}
									style={{
										cursor: 'pointer',
										marginLeft: 10,
										marginBottom: 2,
										color: '#d32f2f',
										display: 'block',
									}}
									onClick={() => handleRemoveItem(field.value)}
									role="button"
									tabIndex={0}
								>
									<Text color="success">{field.label}</Text>
								</span>
							))}
						</div>
					))}
				</Layout>
			)}
		</Layout>
	);
};